import React from 'react';
import { Button, CircularProgress, Box } from '@mui/material';
import { useActionLock } from '../hooks/useActionLock';

/**
 * Action button with built-in click-lock and loading spinner.
 * Use for any action that triggers API calls (Save, Submit, Delete, Confirm, Upload, etc.)
 * to prevent duplicate requests and duplicate data.
 *
 * Props:
 *   onClick - Async function (or function returning Promise). Called on click; button is disabled until it settles.
 *   loading - Optional. If provided, use as controlled loading state (parent manages lock).
 *   loadingText - Text to show next to spinner when loading (e.g. "Saving...", "Deleting...").
 *   children - Button label when not loading.
 *   ...rest - Any MUI Button props (variant, disabled, fullWidth, sx, etc.)
 *
 * When loading is not passed, ActionButton uses useActionLock internally and wraps onClick.
 * When loading is passed, parent is responsible for setting it (e.g. via useActionLock or existing state).
 */
const ActionButton = ({
  onClick,
  loading: controlledLoading,
  loadingText,
  children,
  disabled,
  startIcon,
  ...rest
}) => {
  const [internalLocked, runAction] = useActionLock();
  const isControlled = controlledLoading !== undefined && controlledLoading !== null;
  const loading = isControlled ? controlledLoading : internalLocked;

  const handleClick = async (e) => {
    if (loading || disabled) return;
    if (!onClick) return;

    if (isControlled) {
      await Promise.resolve(onClick(e));
      return;
    }
    await runAction(() => Promise.resolve(onClick(e)));
  };

  return (
    <Button
      {...rest}
      disabled={disabled || loading}
      onClick={handleClick}
      startIcon={
        loading ? (
          <CircularProgress size={16} sx={{ color: 'inherit' }} />
        ) : (
          startIcon
        )
      }
    >
      {loading && loadingText ? (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          {loadingText}
        </Box>
      ) : (
        children
      )}
    </Button>
  );
};

export default ActionButton;
